import type { OfferStoreRestrictionMode } from './is-store-allowed';

export type OfferStoreRestrictions = {
  storeRestrictionMode?: OfferStoreRestrictionMode;
  availableStoreIds?: readonly (string | null | undefined)[] | null;
};

/**
 * Copies store restriction metadata onto an offer that resolved without it.
 *
 * The loyalty service only populates `storeRestrictionMode` and
 * `availableStoreIds` on the single-offer query when `storeId` is omitted, so a
 * store-scoped result never carries the restriction the restaurant selector
 * needs to filter ineligible restaurants.
 *
 * `AppOffer` does not declare these fields even though `mapServiceOffer` copies
 * them from the service, so they are read through a structural view.
 */
export function applyOfferStoreRestrictions<T extends object>(
  offer: T | undefined,
  restrictionSource: object | undefined
): T | undefined {
  const source = restrictionSource as OfferStoreRestrictions | undefined;

  if (
    !offer ||
    (offer as OfferStoreRestrictions).storeRestrictionMode ||
    !source?.storeRestrictionMode
  ) {
    return offer;
  }

  return {
    ...offer,
    storeRestrictionMode: source.storeRestrictionMode,
    availableStoreIds: (source.availableStoreIds ?? []).filter((id): id is string => Boolean(id)),
  } as T;
}
